import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { MessageCircle, Send, Check, CheckCheck, Sparkles, ClipboardList, Moon, Apple, HeartPulse } from "lucide-react";

const CAT_ICON = { "récupération": HeartPulse, "recuperation": HeartPulse, "nutrition": Apple, "sommeil": Moon, "sleep": Moon };
const iconFor = (cat) => CAT_ICON[(cat || "").toLowerCase()] || ClipboardList;

export default function CoachChannel() {
  const [data, setData] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/coach-chat").then((r) => setData(r.data)).catch(() => setData({ enabled: false })), []);
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [data?.messages?.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput(""); setSending(true);
    try {
      const { data: msg } = await api.post("/coach-chat", { text });
      setData((d) => ({ ...d, messages: [...(d?.messages || []), msg] }));
    } catch { /* ignore */ } finally { setSending(false); }
  };

  if (!data) return <ClientLayout><div className="mt-16 pulse-organic w-3 h-3 rounded-full bg-obsidian mx-auto" /></ClientLayout>;

  if (!data.enabled) {
    return (
      <ClientLayout>
        <div className="mt-6 rounded-2xl border border-border bg-white p-8 text-center shadow-premium" data-testid="coachchat-locked">
          <span className="mx-auto w-12 h-12 rounded-full bg-sage/15 flex items-center justify-center"><MessageCircle size={22} className="text-sage" /></span>
          <h1 className="mt-4 text-xl font-medium">Chat direct avec Ton Coach</h1>
          <p className="mt-2 text-sm text-obsidian/60">Échange en direct avec Ton Coach et reçois des consignes personnalisées (récupération, nutrition, sommeil). Cette prestation est incluse dans l'offre <strong>Premium</strong>.</p>
          <button data-testid="coachchat-upgrade" onClick={() => navigate("/tarifs")} className="mt-5 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">Passer à Premium</button>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout fill>
      <div className="flex flex-col h-full" data-testid="coachchat-page">
        <div className="shrink-0 pt-4 pb-3">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-sage/15 flex items-center justify-center"><MessageCircle size={18} className="text-sage" /></span>
            <div><h1 className="text-lg font-medium leading-tight">Ton Coach</h1><div className="text-xs text-obsidian/50">Canal direct · réponses sous 24-48h</div></div>
          </div>

          {data.rules?.length > 0 && (
            <div className="mt-3 rounded-2xl border border-sage/30 bg-sage/[0.06] p-4" data-testid="coach-rules">
              <div className="flex items-center gap-2 text-xs tracking-wide uppercase font-semibold text-sage"><Sparkles size={14} /> Consignes de Ton Coach</div>
              <div className="mt-2.5 space-y-2.5">
                {data.rules.map((r, i) => { const I = iconFor(r.category); return (
                  <div key={r.id || i} className="flex gap-2.5" data-testid={`rule-${i}`}>
                    <I size={16} className="text-sage shrink-0 mt-0.5" />
                    <div><div className="text-sm font-medium">{r.title || r.category}</div><div className="text-sm text-obsidian/70 leading-relaxed">{r.text}</div></div>
                  </div>
                ); })}
              </div>
            </div>
          )}
        </div>

        <div ref={scrollRef} data-testid="coachchat-messages" className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-3 space-y-3">
          {data.messages.length === 0 && <div className="text-center text-sm text-obsidian/40 mt-8">Démarre la conversation avec Ton Coach 👋</div>}
          {data.messages.map((m) => (
            <div key={m.message_id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed ${m.sender === "client" ? "bg-obsidian text-white rounded-tr-sm" : "bg-white border border-border rounded-tl-sm"}`}>
                <div>{m.text}</div>
                {m.sender === "client" && (
                  <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-white/60">
                    {m.read_at ? <><CheckCheck size={13} /> Lu</> : <><Check size={13} /> Envoyé</>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="shrink-0 flex items-end gap-2.5 py-3 border-t border-border">
          <textarea data-testid="coachchat-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Écris à Ton Coach…" className="flex-1 resize-none rounded-2xl border border-border bg-white px-4 py-3 text-[15px] focus:ring-1 focus:ring-obsidian focus:outline-none" />
          <button data-testid="coachchat-send" onClick={send} disabled={sending || !input.trim()} className="w-11 h-11 shrink-0 rounded-full bg-obsidian text-white flex items-center justify-center disabled:opacity-30 hover:-translate-y-0.5 transition-transform"><Send size={17} /></button>
        </div>
      </div>
    </ClientLayout>
  );
}
